import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Palette } from 'lucide-react';

interface ColorPickerProps {
  label: string;
  value?: string;
  onChange: (color: string) => void;
  showOpacity?: boolean;
  allowClear?: boolean;
  textMode?: boolean; // When true, don't close when clicking on contentEditable areas
}

const DEFAULT_COLORS = [
  '#ffffff', '#f3f4f6', '#e5e7eb', '#d1d5db', '#9ca3af', '#6b7280', '#374151', '#111827',
  '#fef2f2', '#fee2e2', '#fecaca', '#f87171', '#ef4444', '#dc2626', '#b91c1c', '#991b1b',
  '#fff7ed', '#fed7aa', '#fdba74', '#fb923c', '#f97316', '#ea580c', '#c2410c', '#9a3412',
  '#fffbeb', '#fef3c7', '#fde68a', '#fcd34d', '#f59e0b', '#d97706', '#b45309', '#92400e',
  '#f0fdf4', '#dcfce7', '#bbf7d0', '#86efac', '#4ade80', '#22c55e', '#16a34a', '#15803d',
  '#ecfdf5', '#d1fae5', '#a7f3d0', '#6ee7b7', '#34d399', '#10b981', '#059669', '#047857',
  '#f0fdfa', '#ccfbf1', '#99f6e4', '#5eead4', '#2dd4bf', '#14b8a6', '#0d9488', '#0f766e',
  '#ecfeff', '#cffafe', '#a5f3fc', '#67e8f9', '#22d3ee', '#06b6d4', '#0891b2', '#0e7490',
  '#f0f9ff', '#e0f2fe', '#bae6fd', '#7dd3fc', '#38bdf8', '#0ea5e9', '#0284c7', '#0369a1',
  '#eff6ff', '#dbeafe', '#bfdbfe', '#93c5fd', '#60a5fa', '#3b82f6', '#2563eb', '#1d4ed8',
  '#eef2ff', '#e0e7ff', '#c7d2fe', '#a5b4fc', '#818cf8', '#6366f1', '#4f46e5', '#4338ca',
  '#f3e8ff', '#e9d5ff', '#d8b4fe', '#c084fc', '#a855f7', '#9333ea', '#7c3aed', '#6d28d9',
  '#faf5ff', '#fdf4ff', '#f5f3ff', '#ede9fe', '#ddd6fe', '#c4b5fd', '#a78bfa', '#8b5cf6',
  '#fdf2f8', '#fce7f3', '#fbcfe8', '#f9a8d4', '#f472b6', '#ec4899', '#db2777', '#be185d'
];

export default function ColorPicker({ label, value = '#ffffff', onChange, showOpacity = true, allowClear = false, textMode = false }: ColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [customColor, setCustomColor] = useState(value.includes('rgba') ? '#ffffff' : value.split('(')[0] || '#ffffff');
  const [opacity, setOpacity] = useState(() => {
    if (value.includes('rgba')) {
      const match = value.match(/rgba\([^,]+,[^,]+,[^,]+,([^)]+)\)/);
      return match ? parseFloat(match[1]) * 100 : 100;
    }
    return 100;
  });
  const [showAbove, setShowAbove] = useState(false);
  const [_positionLeft, setPositionLeft] = useState(true);
  const [hexInput, setHexInput] = useState(value.includes('rgba') ? customColor : value);
  const [isEditingHex, setIsEditingHex] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });


  const colorPickerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Calculate portal positioning when picker opens
  const calculatePortalPosition = () => {
    if (!colorPickerRef.current) return;
    
    const buttonRect = colorPickerRef.current.getBoundingClientRect();
    const dropdownWidth = 320;
    const padding = 10; // Minimum distance from viewport edges
    
    // Get actual dropdown height from rendered element
    const dropdownHeight = dropdownRef.current?.offsetHeight || 500; // Fallback if not yet rendered
    
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Calculate optimal horizontal position
    let left = buttonRect.left;
    
    // Adjust horizontal position if dropdown would go off-screen
    if (left + dropdownWidth > viewportWidth - padding) {
      // Try positioning from right edge of button
      left = buttonRect.right - dropdownWidth;
    }
    
    // If still off-screen on the left, push it right
    if (left < padding) {
      left = padding;
    }
    
    // If dropdown is wider than viewport, center it
    if (dropdownWidth > viewportWidth - (padding * 2)) {
      left = (viewportWidth - dropdownWidth) / 2;
    }
    
    // Calculate optimal vertical position
    const spaceBelow = viewportHeight - buttonRect.bottom - padding;
    const spaceAbove = buttonRect.top - padding;
    
    let top;
    let showAbove = false;
    
    if (spaceBelow >= dropdownHeight) {
      // Enough space below - position below button
      top = buttonRect.bottom + 4;
      showAbove = false;
    } else if (spaceAbove >= dropdownHeight) {
      // Not enough space below but enough above - position above button
      top = buttonRect.top - dropdownHeight - 4;
      showAbove = true;
    } else {
      // Not enough space in either direction - position where there's more space
      if (spaceBelow > spaceAbove) {
        // More space below - position at bottom edge of viewport
        top = viewportHeight - dropdownHeight - padding;
        showAbove = false;
      } else {
        // More space above - position at top edge of viewport
        top = padding;
        showAbove = false;
      }
    }
    
    // Final safety check - ensure dropdown is fully within viewport
    const maxTop = viewportHeight - dropdownHeight - padding;
    top = Math.max(padding, Math.min(top, maxTop));
    
    // Debug logging (remove in production)
    // console.log('ColorPicker positioning:', {
    //   buttonRect: { bottom: buttonRect.bottom, top: buttonRect.top },
    //   dropdownHeight,
    //   viewportHeight,
    //   spaceBelow: viewportHeight - buttonRect.bottom - padding,
    //   spaceAbove: buttonRect.top - padding,
    //   calculatedTop: top,
    //   maxTop,
    //   finalPosition: { top, left }
    // });
    
    setShowAbove(showAbove);
    setDropdownPosition({ top, left });
  };

  // Update hexInput when value changes from outside
  useEffect(() => {
    if (!isEditingHex) {
      setHexInput(value.includes('rgba') ? customColor : value);
    }
  }, [value, customColor, isEditingHex]);

  // Close color picker when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Don't close if clicking inside the color picker trigger area
      if (colorPickerRef.current && colorPickerRef.current.contains(target)) {
        return;
      }
      
      // Don't close if clicking inside the dropdown portal
      if (dropdownRef.current && dropdownRef.current.contains(target)) {
        return;
      }
      
      // In text mode, allow closing when clicking anywhere in contentEditable
      // The color picker should close when user clicks elsewhere to make a new selection
      
      // Close the color picker
      setIsOpen(false);
    };

    // Delay to avoid conflicts with opening click when preventDefault is used
    const delay = textMode ? 200 : 50;
    
    const timeoutId = setTimeout(() => {
      if (isOpen) { // Double-check we're still open
        document.addEventListener('mousedown', handleClickOutside);
      }
    }, delay);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, textMode]);

  // Recalculate position on window resize
  useEffect(() => {
    if (!isOpen) return;
    
    const handleResize = () => {
      calculatePortalPosition();
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  // Calculate derived values
  const displayColor = value.includes('rgba') ? customColor : value;
  const displayOpacity = value.includes('rgba') ? opacity : 100;
  const hasValue = value && value.trim() !== '';

  const handleColorChange = (color: string) => {
    if (showOpacity && opacity < 100) {
      // Convert hex to rgba
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      const a = opacity / 100;
      onChange(`rgba(${r}, ${g}, ${b}, ${a})`);
    } else {
      onChange(color);
    }
  };

  const handleCustomColorChange = (color: string) => {
    setCustomColor(color);
    handleColorChange(color);
  };

  const handleOpacityChange = (newOpacity: number) => {
    setOpacity(newOpacity);
    if (newOpacity === 100) {
      onChange(customColor);
    } else {
      const r = parseInt(customColor.slice(1, 3), 16);
      const g = parseInt(customColor.slice(3, 5), 16);
      const b = parseInt(customColor.slice(5, 7), 16);
      const a = newOpacity / 100;
      onChange(`rgba(${r}, ${g}, ${b}, ${a})`);
    }
  };

  const handleClear = () => {
    onChange('');
    setIsOpen(false);
  };

  const validateHexColor = (hex: string): boolean => {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex);
  };

  const handleHexInputChange = (inputValue: string) => {
    setHexInput(inputValue);
    
    // Auto-add # if not present
    let hexValue = inputValue;
    if (!hexValue.startsWith('#') && hexValue.length > 0) {
      hexValue = '#' + hexValue;
      setHexInput(hexValue);
    }
    
    // If it's a valid hex color, update the color
    if (validateHexColor(hexValue)) {
      setCustomColor(hexValue);
      handleColorChange(hexValue);
    }
  };

  const handleHexInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setIsEditingHex(false);
      setIsOpen(false);
    } else if (e.key === 'Escape') {
      setHexInput(value.includes('rgba') ? customColor : value);
      setIsEditingHex(false);
    }
  };

  const handleHexClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent the dropdown from toggling
    setIsEditingHex(true);
  };


  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-xs text-gray-600 mb-1">
          <Palette className="inline h-3 w-3 mr-1" />
          {label}
        </label>
      )}
      
      <div className="relative" ref={colorPickerRef}>
        <div className="w-full flex items-center space-x-2 p-2 border border-gray-300 rounded-md hover:border-gray-400 transition-colors">
          <div
            className="w-6 h-6 rounded border border-gray-300 flex-shrink-0 cursor-pointer"
            style={{ 
              backgroundColor: hasValue ? value : '#f3f4f6',
              backgroundImage: hasValue ? 'none' : 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
              backgroundSize: hasValue ? 'auto' : '8px 8px',
              backgroundPosition: hasValue ? 'auto' : '0 0, 0 4px, 4px -4px, -4px 0px'
            }}
            onMouseDown={(e) => {
              // In text mode, prevent the mousedown from stealing focus from the editor
              if (textMode) {
                e.preventDefault();
              }
            }}
            onClick={(e) => {
              // In text mode, only open the picker, never close it via click (let click-away handle closing)
              if (textMode) {
                if (!isOpen) {
                  setIsOpen(true);
                  setTimeout(() => {
                    calculatePortalPosition();
                  }, 0);
                }
                // Ignore clicks when already open in text mode
              } else {
                // Normal mode - toggle behavior
                if (!isOpen) {
                  setIsOpen(true);
                  setTimeout(() => {
                    calculatePortalPosition();
                  }, 0);
                } else {
                  setIsOpen(false);
                }
              }
            }}
          />
          
          {!hasValue ? (
            <span 
              className="text-sm text-gray-700 flex-1 text-left cursor-pointer"
              onMouseDown={(e) => {
              if (textMode) {
                e.preventDefault();
              }
            }}
            onClick={() => {
              if (!isOpen) {
                setIsOpen(true);
                setTimeout(() => {
                  calculatePortalPosition();
                }, 0);
              } else {
                setIsOpen(false);
              }
            }}
            >
              None
            </span>
          ) : value.includes('rgba') ? (
            <span 
              className="text-sm text-gray-700 flex-1 text-left cursor-pointer"
              onMouseDown={(e) => {
              if (textMode) {
                e.preventDefault();
              }
            }}
            onClick={() => {
              if (!isOpen) {
                setIsOpen(true);
                setTimeout(() => {
                  calculatePortalPosition();
                }, 0);
              } else {
                setIsOpen(false);
              }
            }}
            >
              Custom ({Math.round(displayOpacity)}% opacity)
            </span>
          ) : isEditingHex ? (
            <input
              type="text"
              value={hexInput}
              onChange={(e) => handleHexInputChange(e.target.value)}
              onKeyDown={handleHexInputKeyDown}
              onBlur={() => setIsEditingHex(false)}
              onMouseDown={(e) => {
                // Allow the input to receive focus, but prevent event from bubbling
                e.stopPropagation();
              }}
              className="flex-1 text-sm text-gray-700 bg-transparent border-none outline-none focus:ring-0 p-0"
              autoFocus
              onFocus={(e) => e.target.select()}
            />
          ) : (
            <span 
              className="text-sm text-gray-700 flex-1 text-left cursor-pointer hover:bg-gray-50 px-1 py-0.5 rounded select-all"
              onMouseDown={(e) => {
                if (textMode) {
                  e.preventDefault();
                  console.log('🔍 Prevented hex span mousedown to preserve selection');
                }
              }}
              onClick={handleHexClick}
              title="Click to edit hex code"
            >
              {value}
            </span>
          )}
        </div>

        {isOpen && createPortal(
            <div 
              ref={dropdownRef}
              className="fixed border border-gray-300 rounded-md shadow-lg p-3"
              style={{ 
                width: '320px', 
                minWidth: '320px',
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                zIndex: 9999,
                backgroundColor: 'white'
              }}
            >
            {/* Default Colors */}
            <div className="mb-3">
              <div className="text-xs text-gray-500 mb-2">Preset Colors</div>
              <div className="grid grid-cols-8 gap-1">
                {DEFAULT_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`w-6 h-6 rounded border-2 hover:scale-110 transition-transform ${
                      displayColor === color ? 'border-gray-600' : 'border-gray-200'
                    }`}
                    style={{ backgroundColor: color }}
                    onMouseDown={(e) => {
                      if (textMode) {
                        e.preventDefault();
                      }
                    }}
                    onClick={() => {
                      setCustomColor(color);
                      handleColorChange(color);
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Custom Color Picker */}
            <div className="mb-3">
              <div className="text-xs text-muted-foreground mb-2">Custom Color</div>
              <input
                type="color"
                value={customColor}
                onChange={(e) => handleCustomColorChange(e.target.value)}
                className="w-full h-8 rounded border border-border cursor-pointer"
              />
            </div>

            {/* Opacity Slider */}
            {showOpacity && (
              <div className="mb-3">
                <div className="text-xs text-muted-foreground mb-2">Opacity: {Math.round(displayOpacity)}%</div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={displayOpacity}
                  onChange={(e) => handleOpacityChange(parseInt(e.target.value))}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
                />
              </div>
            )}

            {/* Clear Button */}
            {allowClear && hasValue && (
              <button
                type="button"
                onMouseDown={(e) => {
                  if (textMode) {
                    e.preventDefault();
                  }
                }}
                onClick={() => {
                  handleClear();
                }}
                className="w-full px-3 py-2 text-xs bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded transition-colors"
              >
                Clear Color
              </button>
            )}
          </div>,
          document.body
        )}
      </div>
    </div>
  );
}
